import { useEffect, useMemo, useState } from "react";
import { api, type MailMessageDetail, type PublicMailAccount } from "../../lib/api";
import { RichMailEditor } from "./RichMailEditor";

export type ComposeMode = "new" | "reply" | "replyAll" | "forward";

function extractEmails(raw: string | undefined | null): string[] {
  if (!raw) return [];
  const found = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of found) {
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

function prefixSubject(subject: string, prefix: "Re:" | "Fwd:") {
  const s = (subject || "").trim();
  if (!s) return prefix === "Re:" ? "Re: " : "Fwd: ";
  if (prefix === "Re:" && /^re:/i.test(s)) return s;
  if (prefix === "Fwd:" && /^(fwd|fw):/i.test(s)) return s;
  return `${prefix} ${s}`;
}

function quoteBlock(source: MailMessageDetail) {
  const when = source.date ? new Date(source.date).toLocaleString() : "";
  const body =
    source.html?.trim() ||
    `<p>${(source.text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>")}</p>`;
  return `<p></p><blockquote><p><em>Il ${when} ${source.from || ""} ha scritto:</em></p>${body}</blockquote>`;
}

function forwardBlock(source: MailMessageDetail) {
  const when = source.date ? new Date(source.date).toLocaleString() : "";
  const body =
    source.html?.trim() ||
    `<p>${(source.text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>")}</p>`;
  return [
    "<p></p>",
    "<hr/>",
    "<p><strong>---------- Messaggio inoltrato ----------</strong></p>",
    `<p><strong>Da:</strong> ${source.from || ""}<br/>`,
    `<strong>Data:</strong> ${when}<br/>`,
    `<strong>Oggetto:</strong> ${source.subject || ""}<br/>`,
    `<strong>A:</strong> ${source.to || ""}`,
    source.cc ? `<br/><strong>Cc:</strong> ${source.cc}` : "",
    "</p>",
    body,
  ].join("");
}

function buildComposeDraft(
  mode: ComposeMode,
  source: MailMessageDetail | null,
  selfEmails: string[],
): { to: string; cc: string; subject: string; html: string; title: string } {
  const self = new Set(selfEmails.map((e) => e.toLowerCase()));
  if (!source || mode === "new") {
    return { to: "", cc: "", subject: "", html: "<p></p>", title: "Nuovo messaggio" };
  }

  if (mode === "forward") {
    return {
      to: "",
      cc: "",
      subject: prefixSubject(source.subject, "Fwd:"),
      html: forwardBlock(source),
      title: "Inoltra",
    };
  }

  const fromEmails = extractEmails(source.from);
  const toEmails = extractEmails(source.to);
  const ccEmails = extractEmails(source.cc);

  if (mode === "reply") {
    return {
      to: fromEmails.join(", "),
      cc: "",
      subject: prefixSubject(source.subject, "Re:"),
      html: quoteBlock(source),
      title: "Rispondi",
    };
  }

  // replyAll: To = From; Cc = (original To + Cc) minus self and From
  const primary = fromEmails[0] ? [fromEmails[0]] : [];
  const ccSet = new Set<string>();
  for (const e of [...fromEmails.slice(1), ...toEmails, ...ccEmails]) {
    const key = e.toLowerCase();
    if (self.has(key)) continue;
    if (primary.some((p) => p.toLowerCase() === key)) continue;
    ccSet.add(e);
  }
  return {
    to: primary.join(", "),
    cc: [...ccSet].join(", "),
    subject: prefixSubject(source.subject, "Re:"),
    html: quoteBlock(source),
    title: "Rispondi a tutti",
  };
}

export function ComposerModal({
  accounts,
  defaultAccountId,
  mode,
  source,
  onClose,
  onSent,
}: {
  accounts: PublicMailAccount[];
  defaultAccountId?: string;
  mode: ComposeMode;
  source: MailMessageDetail | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const accountIdInit = defaultAccountId || accounts[0]?.id || "";
  const selfEmails = useMemo(() => accounts.map((a) => a.email), [accounts]);
  const draft = useMemo(
    () => buildComposeDraft(mode, source, selfEmails),
    [mode, source, selfEmails],
  );

  const [accountId, setAccountId] = useState(accountIdInit);
  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc);
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(Boolean(draft.cc) || mode === "replyAll");
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(draft.subject);
  const [html, setHtml] = useState(draft.html);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [draftTick, setDraftTick] = useState(0);
  const [suggestions, setSuggestions] = useState<Array<{ displayName: string; email: string }>>([]);

  useEffect(() => {
    const t = window.setInterval(() => setDraftTick((n) => n + 1), 15000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!draftTick) return;
    localStorage.setItem(
      "mailstorm.draft",
      JSON.stringify({ accountId, to, cc, bcc, subject, html, mode, at: Date.now() }),
    );
  }, [draftTick, accountId, to, cc, bcc, subject, html, mode]);

  useEffect(() => {
    const q = to.split(",").pop()?.trim() || "";
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void api.suggestContacts(q).then((r) => setSuggestions(r.contacts)).catch(() => setSuggestions([]));
    }, 180);
    return () => window.clearTimeout(handle);
  }, [to]);

  async function send() {
    if (!accountId) {
      setError("Scegli un account mittente");
      return;
    }
    const recipients = to.split(",").map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) {
      setError("Inserisci almeno un destinatario in A");
      return;
    }
    setSending(true);
    setError("");
    try {
      const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      await api.send({
        accountId,
        to: recipients,
        cc: cc
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        bcc: bcc
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        subject,
        html,
        text,
        inReplyTo: mode === "forward" || mode === "new" ? undefined : source?.messageId || undefined,
      });
      localStorage.removeItem("mailstorm.draft");
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal modal-composer" onClick={(e) => e.stopPropagation()}>
        <div className="composer-chrome">
          <div>
            <div className="eyebrow">Compose</div>
            <strong className="composer-title">{draft.title}</strong>
          </div>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Chiudi" type="button">
            ✕
          </button>
        </div>
        <div className="composer">
          <div className="composer-fields">
            <div className="composer-field">
              <label className="label">Da</label>
              <select className="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.email}
                  </option>
                ))}
              </select>
            </div>
            <div className="composer-field">
              <label className="label">A</label>
              <input className="input" value={to} onChange={(e) => setTo(e.target.value)} autoComplete="off" />
              {suggestions.length > 0 && (
                <div className="suggest-list">
                  {suggestions.map((s) => (
                    <button
                      key={s.email}
                      type="button"
                      className="suggest-item"
                      onClick={() => {
                        const parts = to.split(",");
                        parts[parts.length - 1] = ` ${s.email}`;
                        setTo(parts.join(",").replace(/^\s+/, ""));
                        setSuggestions([]);
                      }}
                    >
                      {s.displayName} · {s.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="composer-field-row">
              {!showCc ? (
                <button type="button" className="btn btn-ghost" onClick={() => setShowCc(true)}>
                  Cc
                </button>
              ) : (
                <div className="composer-field" style={{ flex: 1 }}>
                  <label className="label">Cc</label>
                  <input className="input" value={cc} onChange={(e) => setCc(e.target.value)} />
                </div>
              )}
              <button type="button" className="btn btn-ghost" onClick={() => setShowBcc((v) => !v)}>
                {showBcc ? "Nascondi Ccn" : "Ccn"}
              </button>
            </div>
            {showBcc && (
              <div className="composer-field">
                <label className="label">Ccn</label>
                <input className="input" value={bcc} onChange={(e) => setBcc(e.target.value)} />
              </div>
            )}
            <div className="composer-field">
              <label className="label">Oggetto</label>
              <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
          </div>

          <RichMailEditor key={`${mode}-${source?.uid || "new"}-${source?.accountId || ""}`} initialHtml={draft.html} onChange={setHtml} />

          <div className="composer-footer">
            <span className="composer-hint">Bozza locale ogni 15s · Esc chiude</span>
            {error && <div className="error">{error}</div>}
            <div className="composer-actions">
              <button className="btn" type="button" onClick={onClose}>
                Annulla
              </button>
              <button className="btn btn-primary" type="button" onClick={() => void send()} disabled={sending}>
                {sending ? "Invio…" : "Invia"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
